import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const login = (credentials) => api.post('/auth/login', credentials);
export const logout = () => api.post('/auth/logout');
export const verifyToken = () => api.get('/auth/verify');

// Campus APIs
export const getCampuses = () => api.get('/campuses');
export const addCampus = (data) => api.post('/campuses', data);
export const updateCampus = (id, data) => api.put(`/campuses/${id}`, data);
export const deleteCampus = (id) => api.delete(`/campuses/${id}`);

// Course APIs
export const getCourses = () => api.get('/courses');
export const addCourse = (data) => api.post('/courses', data);
export const updateCourse = (id, data) => api.put(`/courses/${id}`, data);
export const deleteCourse = (id) => api.delete(`/courses/${id}`);

// NVQF Level APIs
export const getLevels = () => api.get('/levels');

// Student APIs
export const getStudents = (params) => api.get('/students', { 
  params: {
    ...params,
    _t: Date.now() // Cache buster
  }
});
export const getStudent = (id) => api.get(`/students/${id}`);
export const addStudent = (data) => api.post('/students', data);
export const updateStudent = (id, data) => api.put(`/students/${id}`, data);
export const deleteStudent = (id) => api.delete(`/students/${id}`);
export const bulkUpdateStudents = (ids, updateData) => api.put('/students/bulk-update', { ids, updateData });
export const bulkDeleteStudents = (ids) => api.post('/students/bulk-delete', { ids });
export const importStudents = (formData) => api.post('/students/import', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 600000 // 10 minutes for large imports
});
export const downloadTemplate = () => api.get('/students/template/download', { responseType: 'blob' });
export const exportStudents = (params) => api.get('/students/export/csv', { 
  params,
  responseType: 'blob' 
});

// Hostel portal auth (uses separate token key)
const hostelApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});
hostelApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('hostelToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
hostelApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('hostelToken');
      localStorage.removeItem('hostelUser');
      window.location.href = '/hostel/login';
    }
    return Promise.reject(error);
  }
);

// Hostel Auth
export const hostelLogin = (credentials) => axios.post(`${API_BASE_URL}/hostel-auth/login`, credentials);
export const hostelVerifyToken = () => hostelApi.get('/hostel-auth/verify');

// Hostel CRUD (admin)
export const getHostels = () => api.get('/hostels');
export const getHostel = (id) => api.get(`/hostels/${id}`);
export const addHostel = (data) => api.post('/hostels', data);
export const updateHostel = (id, data) => api.put(`/hostels/${id}`, data);
export const deleteHostel = (id) => api.delete(`/hostels/${id}`);

// Hostel Admissions (admin uses api, hostel portal uses hostelApi)
export const getHostelAdmissions = (params) => api.get('/hostel-admissions', { params });
export const getHostelAdmission = (id) => api.get(`/hostel-admissions/${id}`);
export const addHostelAdmission = (data) => api.post('/hostel-admissions', data);
export const updateHostelAdmission = (id, data) => api.put(`/hostel-admissions/${id}`, data);
export const deleteHostelAdmission = (id) => api.delete(`/hostel-admissions/${id}`);
export const getHostelAdmissionOptions = () => api.get('/hostel-admissions/meta/options');
export const lookupByCnic = (cnic) => api.get('/hostel-admissions/lookup-by-cnic', { params: { cnic } });
export const getResidentHistoryByCnic = (cnic) => api.get('/hostel-admissions/history-by-cnic', { params: { cnic } });
export const importHostelResidents = (formData) => api.post('/hostel-admissions/bulk-import', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 600000
});
export const downloadHostelResidentsTemplate = () => api.get('/hostel-admissions/template/download', { responseType: 'blob' });
export const generateCertificatesFromResidents = (ids) => api.post('/hostel-admissions/generate-certificates', { ids });

// Hostel portal admissions (uses hostelToken)
export const getHostelAdmissionsPortal = (params) => hostelApi.get('/hostel-admissions', { params });
export const getHostelAdmissionPortal = (id) => hostelApi.get(`/hostel-admissions/${id}`);
export const addHostelAdmissionPortal = (data) => hostelApi.post('/hostel-admissions', data);
export const updateHostelAdmissionPortal = (id, data) => hostelApi.put(`/hostel-admissions/${id}`, data);
export const deleteHostelAdmissionPortal = (id) => hostelApi.delete(`/hostel-admissions/${id}`);
export const getHostelAdmissionOptionsPortal = () => hostelApi.get('/hostel-admissions/meta/options');
export const lookupByCnicPortal = (cnic) => hostelApi.get('/hostel-admissions/lookup-by-cnic', { params: { cnic } });
export const importHostelResidentsPortal = (formData) => hostelApi.post('/hostel-admissions/bulk-import', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 600000
});
export const downloadHostelResidentsTemplatePortal = () => hostelApi.get('/hostel-admissions/template/download', { responseType: 'blob' });
export const checkoutHostelAdmission = (id, data) => api.patch(`/hostel-admissions/${id}/checkout`, data);
export const checkoutHostelAdmissionPortal = (id, data) => hostelApi.patch(`/hostel-admissions/${id}/checkout`, data);

// Hostel Payments (admin)
export const getHostelPayments = (params) => api.get('/hostel-payments', { params });
export const addHostelPayment = (data) => api.post('/hostel-payments', data);
export const deleteHostelPayment = (id) => api.delete(`/hostel-payments/${id}`);

// Hostel Payments (portal)
export const getHostelPaymentsPortal = (params) => hostelApi.get('/hostel-payments', { params });

// Hostel Requests
export const getHostelRequests = (params) => api.get('/hostel-requests', { params });
export const submitHostelRequest = (data) => hostelApi.post('/hostel-requests', data);
export const approveHostelRequest = (id, data) => api.put(`/hostel-requests/${id}/approve`, data);
export const rejectHostelRequest = (id, data) => api.put(`/hostel-requests/${id}/reject`, data);
export const getHostelRequestsPortal = (params) => hostelApi.get('/hostel-requests', { params });

// ── Attendance (admin uses api, hostel portal uses hostelApi) ──────────
export const getAttendanceRoster = (params) => api.get('/attendance/roster', { params });
export const getAttendanceHistory = (params) => api.get('/attendance', { params });
export const markAttendance = (data) => api.post('/attendance/mark', data);
export const bulkMarkAttendance = (data) => api.post('/attendance/bulk-mark', data);
export const unmarkAttendance = (data) => api.post('/attendance/unmark', data);
export const bulkUnmarkAttendance = (data) => api.post('/attendance/bulk-unmark', data);

export const getAttendanceRosterPortal = (params) => hostelApi.get('/attendance/roster', { params });
export const getAttendanceHistoryPortal = (params) => hostelApi.get('/attendance', { params });
export const markAttendancePortal = (data) => hostelApi.post('/attendance/mark', data);
export const bulkMarkAttendancePortal = (data) => hostelApi.post('/attendance/bulk-mark', data);
export const unmarkAttendancePortal = (data) => hostelApi.post('/attendance/unmark', data);
export const bulkUnmarkAttendancePortal = (data) => hostelApi.post('/attendance/bulk-unmark', data);
export const requestAttendanceEditPortal = (data) => hostelApi.post('/attendance/request-edit', data);

// ── Institute Attendance (admin-only — cross-checked against hostel attendance) ──
export const getInstituteAttendanceRoster = (params) => api.get('/institute-attendance/roster', { params });
export const getInstituteAttendanceHistory = (params) => api.get('/institute-attendance', { params });
export const markInstituteAttendance = (data) => api.post('/institute-attendance/mark', data);
export const bulkMarkInstituteAttendance = (data) => api.post('/institute-attendance/bulk-mark', data);

// ── Meals ────────────────────────────────────────────────────────────
export const getMealsRoster = (params) => api.get('/meals/roster', { params });
export const getMealsHistory = (params) => api.get('/meals', { params });
export const markMeals = (data) => api.post('/meals/mark', data);
export const bulkMarkMeals = (data) => api.post('/meals/bulk-mark', data);
export const unmarkMeal = (data) => api.post('/meals/unmark', data);
export const bulkUnmarkMeals = (data) => api.post('/meals/bulk-unmark', data);

export const getMealsRosterPortal = (params) => hostelApi.get('/meals/roster', { params });
export const getMealsHistoryPortal = (params) => hostelApi.get('/meals', { params });
export const markMealsPortal = (data) => hostelApi.post('/meals/mark', data);
export const bulkMarkMealsPortal = (data) => hostelApi.post('/meals/bulk-mark', data);
export const requestMealsEditPortal = (data) => hostelApi.post('/meals/request-edit', data);

// ── Hostel Billing config + adjustments + preview ───────────────────
export const getBillingConfig = (hostelId) => api.get(`/hostel-billing/config/${hostelId}`);
export const updateBillingConfig = (hostelId, data) => api.put(`/hostel-billing/config/${hostelId}`, data);
export const updateBillingProfileImage = (hostelId, profile_image) => api.put(`/hostel-billing/config/${hostelId}/profile-image`, { profile_image });
export const previewBill = (data) => api.post('/hostel-billing/preview', data);
export const getBillingAdjustments = (params) => api.get('/hostel-billing/adjustments', { params });
export const addBillingAdjustment = (data) => api.post('/hostel-billing/adjustments', data);
export const deleteBillingAdjustment = (id) => api.delete(`/hostel-billing/adjustments/${id}`);

export const getBillingConfigPortal = (hostelId) => hostelApi.get(`/hostel-billing/config/${hostelId}`);
export const previewBillPortal = (data) => hostelApi.post('/hostel-billing/preview', data);
export const getBillingAdjustmentsPortal = (params) => hostelApi.get('/hostel-billing/adjustments', { params });

// ── Invoices ─────────────────────────────────────────────────────────
export const generateInvoice = (data) => api.post('/invoices/generate', data);
export const bulkGenerateInvoices = (data) => api.post('/invoices/bulk-generate', data);
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);

export const generateInvoicePortal = (data) => hostelApi.post('/invoices/generate', data);
export const bulkGenerateInvoicesPortal = (data) => hostelApi.post('/invoices/bulk-generate', data);
export const getInvoicesPortal = (params) => hostelApi.get('/invoices', { params });
export const getInvoicePortal = (id) => hostelApi.get(`/invoices/${id}`);

// ── Roles & Users (admin only) ──────────────────────────────────────
export const getRoles = () => api.get('/roles');
export const getRoleModules = () => api.get('/roles/modules');
export const addRole = (data) => api.post('/roles', data);
export const updateRole = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole = (id) => api.delete(`/roles/${id}`);

export const getUsers = () => api.get('/users');
export const addUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// ── Audit Log (admin only) ──────────────────────────────────────────
export const getAuditLog = (params) => api.get('/audit-log', { params });
export const getAuditLogFilters = () => api.get('/audit-log/filters');

// ── Dashboard ────────────────────────────────────────────────────────
export const getDashboardSummary = () => api.get('/dashboard/summary');

// ── Notifications (admin only) ──────────────────────────────────────
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadNotificationCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

export default api;
